import { useState } from 'react';
import { CheckCircle } from '@phosphor-icons/react';
import { Button } from 'src/components/Button';
import { useWizard } from './WizardContext';
import { useNavigate } from 'react-router-dom';
import { Card } from 'src/components/Card';
import { Badge } from 'src/components/Badge';
import { Loading } from 'src/components/Loading';
import { useNotification } from 'src/contexts/NotificationContext';
import { Modal } from 'src/components/Modal';
import { useCampaigns } from 'src/hooks/useCampaigns';

export function ConfirmationScreen() {
  const {
    campaignAutomation,
    resetWizard,
    campaign,
    isInitializing,
    setCampaign,
    isEditing,
  } = useWizard();
  const navigate = useNavigate();
  const notification = useNotification();
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const campaignsService = useCampaigns();
  const [activateCampaignModalOpen, setActivateCampaignModalOpen] =
    useState(false);
  const [deactivateCampaignModalOpen, setDeactivateCampaignModalOpen] =
    useState(false);

  const refetchCampaign = async () => {
    const updatedCampaign = await campaignsService.fetchCampaign(
      campaign!.referenceId,
    );
    setCampaign(updatedCampaign);
  };

  const handleDeactivateCampaign = async () => {
    try {
      await campaignsService.deactivateCampaign(campaign!.referenceId);
      await refetchCampaign();
      notification.success('Successfully deactivated campaign');
    } catch (error) {
      notification.error('Failed to deactivate campaign');
      console.error(error);
    }
  };

  const handleActivateCampaign = async () => {
    try {
      await campaignsService.activateCampaign(campaign!.referenceId);
      await refetchCampaign();
      notification.success('Successfully activated campaign');
    } catch (error) {
      notification.error('Failed to activate campaign');
      console.error(error);
    }
  };

  const handleActivateToggle = () => {
    if (campaign?.isActive) {
      setDeactivateCampaignModalOpen(true);
    } else {
      setActivateCampaignModalOpen(true);
    }
  };

  const confirmDeactivateCampaign = async () => {
    setIsTogglingActive(true);
    await handleDeactivateCampaign();
    setIsTogglingActive(false);
    setDeactivateCampaignModalOpen(false);
  };

  const confirmActivateCampaign = async () => {
    setIsTogglingActive(true);
    await handleActivateCampaign();
    setIsTogglingActive(false);
    setActivateCampaignModalOpen(false);
  };

  const handleAddContacts = async () => {
    navigate(`/campaigns/${campaign?.referenceId}/contacts`);
  };

  const handleCreateAnother = () => {
    resetWizard();
  };

  const handleViewAll = () => {
    navigate('/campaigns');
  };

  if (isInitializing) {
    return (
      <div className="flex gap-2 items-center justify-center">
        <Loading size="8" />
        <p className="text-neutral-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="text-center py-6">
      <CheckCircle className="h-16 w-16 text-success mx-auto mb-4" />
      <h2 className="mb-2">
        {isEditing
          ? 'Campaign Successfully Updated!'
          : 'Campaign Successfully Created!'}
      </h2>
      <p className="mb-8">
        "{campaignAutomation?.name}" has been set up and is ready to use.
      </p>

      <div className="max-w-3xl mx-auto mb-8">
        {/* Campaign Status Card */}
        <Card className="w-full mb-6 shadow-md border border-neutral-200 bg-neutral-0 text-left">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <Badge variant={campaign?.isActive ? 'success' : 'warning'}>
                  <span
                    className={`w-2 h-2 rounded-full mr-2 inline-block ${campaign?.isActive ? 'bg-success-400' : 'bg-warning-400'}`}
                  ></span>
                  {campaign?.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <h3 className="font-semibold mb-4">Campaign Status</h3>
              <p>
                {campaign?.isActive
                  ? 'This campaign is active. All communications will proceed as scheduled.'
                  : "This campaign is inactive. All communications are on hold until it's activated."}
              </p>
            </div>
          </div>
          <div>
            <Button
              onClick={handleActivateToggle}
              disabled={isTogglingActive}
              variant={campaign?.isActive ? 'outlined' : 'contained'}
            >
              {isTogglingActive && <Loading size="4" className="mr-2" />}
              {isTogglingActive
                ? 'Processing...'
                : campaign?.isActive
                  ? 'Deactivate Campaign'
                  : 'Activate Campaign'}
            </Button>
          </div>
        </Card>

        {/* Activate/Deactivate Campaign Modals */}
        <Modal
          title={`Deactivate Campaign: ${campaignAutomation?.name || ''}`}
          isOpen={deactivateCampaignModalOpen}
          onClose={() => setDeactivateCampaignModalOpen(false)}
          actionButton={
            <Button
              onClick={confirmDeactivateCampaign}
              disabled={isTogglingActive}
            >
              {isTogglingActive ? <Loading size="4" className="mr-2" /> : null}
              Deactivate Campaign
            </Button>
          }
          className="w-auto max-w-md"
        >
          <div className="text-center">
            Are you sure you want to deactivate this campaign?
          </div>
        </Modal>
        <Modal
          title={`Activate Campaign: ${campaignAutomation?.name || ''}`}
          isOpen={activateCampaignModalOpen}
          onClose={() => setActivateCampaignModalOpen(false)}
          actionButton={
            <Button
              onClick={confirmActivateCampaign}
              disabled={isTogglingActive}
            >
              {isTogglingActive ? <Loading size="4" className="mr-2" /> : null}
              Activate Campaign
            </Button>
          }
          className="w-auto max-w-md"
        >
          <div className="text-center">
            Are you sure you want to activate this campaign?
          </div>
        </Modal>

        {/* Add Contacts Card */}
        <Card className="w-full mb-6 shadow-md border border-neutral-200 bg-neutral-50 text-left">
          <h3 className="font-semibold mb-4">
            Add Contacts from a CSV or your CRM
          </h3>
          <p className="mb-4">
            Would you like to add contacts to this campaign from a CSV file or
            sync contacts from your CRM?
          </p>
          <Button onClick={handleAddContacts}>Add Contacts</Button>
        </Card>
      </div>

      <div className="mt-4">
        <Button
          variant="outlined"
          onClick={handleCreateAnother}
          className="mr-2"
        >
          Create Another Campaign
        </Button>
        <Button onClick={handleViewAll}>View All Campaigns</Button>
      </div>
    </div>
  );
}
